import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getWorkspaceSettings,
  updateWorkspaceSettings,
  workspaceSettingsKeys,
} from '@/services/db/environment/queries';
import type { ObjectType } from '@/services/db/object-type/entity';

/** The table's attribute columns, picked from the type's relationship attributes. */
export const MAX_RELATIONSHIP_COLUMNS = 4;

/** The workspace setting holding each object type's displayed relationship columns. */
export const RELATIONSHIP_COLUMNS_SETTING = 'relationship_columns' as const;

type WorkspaceSettingsResponse = Awaited<ReturnType<typeof getWorkspaceSettings>>;

/**
 * The relationship attribute columns this object type's table displays: a
 * workspace setting (every teammate sees the same columns), keyed by type id.
 *
 * A saved column the type no longer sends is left out rather than rendered
 * empty; Ember writes the trimmed list back on mount, where this drops it on
 * read and the next save stores the trimmed list.
 */
export function useRelationshipColumns(workspaceId: number, objectType: ObjectType): readonly string[] {
  const settings = useQuery({
    queryKey: workspaceSettingsKeys.detail(workspaceId),
    queryFn: ({ signal }) => getWorkspaceSettings(workspaceId, signal),
    staleTime: 300_000,
  });

  const saved = settings.data?.settings[RELATIONSHIP_COLUMNS_SETTING]?.[objectType.id];
  const sent = objectType.sent_rel_attributes;
  return useMemo(() => {
    const sentSet = new Set(sent ?? []);
    return (saved ?? []).filter((column) => sentSet.has(column));
  }, [saved, sent]);
}

/**
 * Save the type's displayed columns. The settings endpoint merges the map per
 * type id, so only this type's entry is sent.
 */
export function useSaveRelationshipColumns(workspaceId: number, objectTypeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (columns: string[]) =>
      updateWorkspaceSettings({
        workspaceId,
        settings: { [RELATIONSHIP_COLUMNS_SETTING]: { [objectTypeId]: columns } },
      }),
    onSuccess: (data, columns) => {
      queryClient.setQueryData<WorkspaceSettingsResponse>(workspaceSettingsKeys.detail(workspaceId), (previous) => ({
        ...previous,
        ...data,
        settings: {
          ...previous?.settings,
          ...data.settings,
          [RELATIONSHIP_COLUMNS_SETTING]: {
            ...previous?.settings[RELATIONSHIP_COLUMNS_SETTING],
            ...data.settings[RELATIONSHIP_COLUMNS_SETTING],
            [objectTypeId]: columns,
          },
        },
      }));
    },
  });
}
